import { get, post, put, del } from '../client'
import {
  PressReleaseFilterParamsSchema,
  PaginationParamsSchema,
  CreatePressReleaseSchema,
  UpdatePressReleaseSchema,
  type PressRelease,
  type PressReleaseDetail,
  type PressReleaseFilterParams,
  type CreatePressRelease,
  type UpdatePressRelease,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '../types'

export class PressReleaseService {
 
  async getPressReleases(
    params: PaginationParams = { page: 1, size: 10 },
    filters: PressReleaseFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = PressReleaseFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<PressRelease>>>(
      `press-release/private/filter?${query}`,
      validatedFilters
    )
  }


  async createPressRelease(data: CreatePressRelease): Promise<ApiResponse<PressReleaseDetail>> {

    const validatedData = CreatePressReleaseSchema.parse(data)

    
    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag, // Array of strings
      landingPage: validatedData.landingPage,
      pressRelease: validatedData.pressRelease,
      pressReleaseBanner: validatedData.pressReleaseBanner,
      allowComment: validatedData.allowComment,
      content: validatedData.content,
      status: validatedData.status,
      coverImageUrl: validatedData.coverImageUrl // Image URL string - API expects this field name
    }

    return await post<ApiResponse<PressReleaseDetail>>(
      'press-release/admin/private',
      payload
    )
  }


  async updatePressRelease(
    pressReleaseId: string,
    data: UpdatePressRelease
  ): Promise<ApiResponse<null>> {
    // Validate data
    const validatedData = UpdatePressReleaseSchema.parse(data)

    // Use JSON body instead of FormData since we're sending URLs
    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag, // Array of strings
      landingPage: validatedData.landingPage,
      pressRelease: validatedData.pressRelease,
      pressReleaseBanner: validatedData.pressReleaseBanner,
      allowComment: validatedData.allowComment,
      content: validatedData.content,
      ...(validatedData.status && { status: validatedData.status }),
      ...(validatedData.coverImageUrl && { coverImageUrl: validatedData.coverImageUrl })
    }

    return await put<ApiResponse<null>>(
      `press-release/admin/private/${pressReleaseId}`,
      payload
    )
  }


  async updatePressReleaseStatus(pressReleaseId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<PressRelease>> {
    return await put<ApiResponse<PressRelease>>(
      `press-release/admin/private/${pressReleaseId}/status`,
      { status }
    )
  }

 
  async deletePressRelease(pressReleaseId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `press-release/admin/private/${pressReleaseId}`
    )
  }


  async getAllPressReleases(): Promise<ApiResponse<PressRelease[]>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }


  async getPressReleaseById(pressReleaseId: string): Promise<ApiResponse<PressReleaseDetail>> {
    return await get<ApiResponse<PressReleaseDetail>>(
      `press-release/private/${pressReleaseId}`
    )
  }


  async searchPressReleases(_searchBy: string): Promise<ApiResponse<PressRelease[]>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }


  async getTotalPressReleases(): Promise<ApiResponse<{ count: number }>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }


  async getTotalPublishedPressReleases(): Promise<ApiResponse<{ publishedPressReleases: number }>> {
    return await get<ApiResponse<{ publishedPressReleases: number }>>(
      'press-release/private/stats/published'
    )
  }

 
  async getTotalDraftPressReleases(): Promise<ApiResponse<{ draftPressReleases: number }>> {
    return await get<ApiResponse<{ draftPressReleases: number }>>(
      'press-release/private/stats/draft'
    )
  }


  async getArticleTags(): Promise<ApiResponse<string[]>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }


  async getPublicPressReleases(): Promise<ApiResponse<PressRelease[]>> {
    throw new Error('Not yet implemented - waiting for endpoint documentation')
  }
}


export const pressReleaseService = new PressReleaseService()

// Export default
export default pressReleaseService
